import Popover from "antd/es/popover"
import { useCallback, useState } from "react"

import GongLogo from "../assets/img/gong_logo.svg"
import type { CredentialMetadata } from "../credentials/providerTypes"
import useErrorPopup from "../hooks/useErrorPopup"
import { removeSource } from "../sources/api"
import type { GongSource } from "../sources/types"
import GongPicker from "./GongPicker"
import IntegrationBlock from "./IntegrationBlock"

interface Props {
  source: GongSource | undefined
  loading: boolean
  credential: CredentialMetadata | undefined
  connectCredential: () => Promise<void>
  disconnectCredential: () => Promise<void>
  notEnabled?: boolean
}

const GongIntegrationBlock: React.FC<Props> = ({
  source,
  loading,
  credential,
  connectCredential,
  disconnectCredential,
  notEnabled,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [processing, setProcessing] = useState(false)

  const { handleSuccess, handleError } = useErrorPopup()

  const onEdit = useCallback(() => {
    setPickerOpen(true)
  }, [])

  const missingCredential = !credential
  const missingSource = !source

  const connectSource = useCallback(async () => {
    setProcessing(true)
    try {
      if (missingCredential) {
        await connectCredential()
      }
      onEdit()
    } catch (error) {
      handleError({ error, prefix: "Failed to connect Gong" })
    } finally {
      setProcessing(false)
    }
  }, [connectCredential, missingCredential, onEdit, handleError])

  const hidePicker = useCallback(() => {
    setPickerOpen(false)
  }, [])

  const onDisconnect = useCallback(async () => {
    setProcessing(true)
    try {
      if (!missingSource) {
        await removeSource(source.oid)
      }
      if (!missingCredential) {
        await disconnectCredential()
      }
      handleSuccess(
        "Successfully disconnected. Your calls will be removed in the background",
      )
    } catch (error) {
      handleError({ error, prefix: "Failed to disconnect Gong" })
    } finally {
      setProcessing(false)
    }
  }, [
    source?.oid,
    missingSource,
    missingCredential,
    disconnectCredential,
    handleSuccess,
    handleError,
  ])

  return (
    <Popover
      content={<GongPicker source={source} onClose={hidePicker} />}
      onOpenChange={setPickerOpen}
      open={pickerOpen}
      trigger="click"
    >
      <IntegrationBlock
        loading={loading || processing}
        name="Gong"
        connected={!missingCredential}
        onEdit={onEdit}
        onConnect={connectSource}
        onDisconnect={onDisconnect}
        logo={GongLogo}
        notEnabled={notEnabled}
        disabledReason={
          source?.removal_requested
            ? "Removal requested"
            : pickerOpen
              ? "Use the form above to modify the source"
              : undefined
        }
      />
    </Popover>
  )
}
export default GongIntegrationBlock
